/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';

import type { AudiFootnoteReferenceServiceInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import type { ScopedDisclaimerManager } from '@volkswagen-onehub/disclaimer-manager';
import styled from 'styled-components';
import type { DynamicFootnoteServiceV1 } from '@oneaudi/dynamic-footnote-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { fetchContent } from '@oneaudi/falcon-tools';
import { ProgressIndicator } from '@oneaudi/unified-web-components';
import type { Footnote } from '../../types/footnotes-response.type';
import AudiFootnoteEngine from '../audi-footnote-engine/audi-footnote-engine';
import type {
  CollectionContentHeadless,
  FefaProps,
  FootnoteFields,
} from '../../utils/normalize-falcon-data';
import { normalizeContentHeadlessCollection } from '../../utils/normalize-falcon-data';
import type { CustomTheme } from '../../../@types/theme';

export interface AudiFootnoteEngineContainerProps {
  apiUrls?: string[];
  audiUiTheme: CustomTheme;
  disclaimerManager: ScopedDisclaimerManager;
  disableBottomSpacing: boolean;
  disableSideSpacing: boolean;
  error?: Error | null;
  isServer?: boolean;
  layerElementClassName?: string;
  referenceServiceManager: AudiFootnoteReferenceServiceInterfaceV3;
  configProvider: GfaServiceConfigProviderV1;
  footnoteService?: AudiFootnoteServiceInterfaceV1;
  dynamicFootnoteService?: DynamicFootnoteServiceV1;
  featureAppId: string;
  footnoteData?: FefaProps;
  isOneLayer?: boolean;
}

const LoadingWrapper = styled.div`
  text-align: center;
`;

const AudiFootnoteEngineContainer: React.FC<AudiFootnoteEngineContainerProps> = (props) => {
  const {
    apiUrls,
    audiUiTheme,
    disableBottomSpacing,
    disableSideSpacing,
    disclaimerManager,
    error,
    featureAppId,
    footnoteService,
    isServer,
    layerElementClassName,
    referenceServiceManager,
    footnoteData,
    dynamicFootnoteService,
    configProvider,
    isOneLayer,
  } = props;
  const [componentError, setComponentError] = useState<Error>(error as Error);
  const [componentData, setComponentData] = useState<Footnote[] | null>(null);
  const [fnData, setfnData] = useState<FootnoteFields[] | null | FefaProps>(null);
  const contentApiConfig = configProvider?.audi['falcon-content-api'];

  function hasData<T>(array: T[] | undefined): array is T[] {
    return Array.isArray(array) && array.length > 0;
  }
  const hasFootnoteCollection = hasData(footnoteData?.footnotes);
  const getFootnotes = async (urls: string[]) => {
    const responses = await Promise.all(
      urls?.map(async (url) => {
        try {
          // @ts-expect-error cannot change content fragment type
          const response: CollectionContentHeadless = await fetchContent(
            url.trim(),
            contentApiConfig,
          );

          return normalizeContentHeadlessCollection(response);
        } catch (err) {
          console.error(`Error fetching content for URL: ${url}`, err);
          return []; // Return a default value (e.g., empty array) in case of an error
        }
      }),
    );
    const combinedFootnotes: Footnote[] = responses.flat();
    setfnData(combinedFootnotes);
  };

  useEffect(() => {
    if (isOneLayer) {
      // @ts-expect-error cannot change content fragment type
      getFootnotes(apiUrls);
    }
    if (footnoteData?.urls && !hasFootnoteCollection) {
      getFootnotes(footnoteData?.urls);
    }
  }, [footnoteData?.urls, isOneLayer]);

  useEffect(() => {
    if (footnoteService) {
      try {
        footnoteService.registerCallback((footnotes) => {
          setComponentData(footnotes);
        });
      } catch (e /* istanbul ignore next */) {
        console.warn(e);
      }
    }

    return (): void => {
      if (footnoteService) {
        footnoteService.unregisterCallback();
      }
    };
  }, [footnoteService]);

  useEffect(() => {
    if (componentError === null && componentData === null && footnoteService) {
      if (apiUrls && !isOneLayer) {
        /* in headfull cms, footnotes should be retrieved on client side by using API url */
        (async (): Promise<void> => {
          footnoteService.init(apiUrls);
          const isInitialized = await footnoteService.isInitialized();

          if (isInitialized) {
            const data = footnoteService.getData();
            setComponentData(data);
          } else {
            const error_ = Error('[footnote-engine] Could not initialize footnote-service.');
            setComponentError(error_);
          }
        })();
      } else if (hasFootnoteCollection || fnData !== null) {
        /* in headless cms, footnotes should be retrieved using content-service */
        const data = fnData || footnoteData?.footnotes;
        try {
          // @ts-expect-error incompatible types
          footnoteService?.initWithFootnotes(data);
          const isInitialized = Promise.resolve(footnoteService.isInitialized());
          isInitialized.then((init) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            init
              ? setComponentData(footnoteService.getData())
              : setComponentError(
                  Error('[footnote-service] could not initialize footnote-service.'),
                );
          });
        } catch (e) {
          console.warn('[fa-footnote-engine]', e);
          // @ts-expect-error incompatible types
          setComponentData(data);
        }
      }
    }
  }, [apiUrls, componentData, componentError, footnoteService, isServer, footnoteData, fnData]);

  if (componentError !== null) {
    // TODO style error and add error message
    return <div>Error</div>;
  }

  return (
    <ThemeProvider theme={audiUiTheme}>
      {componentData === null || isServer ? (
        <LoadingWrapper>
          <ProgressIndicator
            aria-label="Loading"
            colorScheme="inverted"
            data-testid="fefa-progress-indicator"
            label="Loading.."
            size="md"
            type="infinite"
            variant="spinner"
          />
        </LoadingWrapper>
      ) : (
        <AudiFootnoteEngine
          disableBottomSpacing={disableBottomSpacing}
          disableSideSpacing={disableSideSpacing}
          disclaimerManager={disclaimerManager}
          featureAppId={featureAppId}
          footnotes={componentData}
          layerElementClassName={layerElementClassName}
          referenceServiceManager={referenceServiceManager}
          replaceFootnotes={footnoteService?.replaceFootNotes}
          dynamicFootnoteService={dynamicFootnoteService}
        />
      )}
    </ThemeProvider>
  );
};

export default AudiFootnoteEngineContainer;
